// @ts-nocheck -- staged migration: add explicit types before removing this directive.
const consonantPattern = /^[bcdfghjklmnpqrstvwxyz]$/;
const vowelPattern = /^[aiueo]$/;

export function buildRomajiMap(characters) {
    return characters.reduce((acc, item) => {
        if (item?.romaji) {
            const key = String(item.romaji).toLowerCase();
            if (!acc[key]) {
                acc[key] = item.symbol;
            }

            const aliasMap = {
                shi: ["si"],
                chi: ["ti"],
                tsu: ["tu"],
                fu: ["hu"],
                ji: ["zi"],
                sha: ["sya"],
                shu: ["syu"],
                sho: ["syo"],
                ja: ["jya", "zya"],
                ju: ["jyu", "zyu"],
                jo: ["jyo", "zyo"],
                cha: ["cya", "tya"],
                chu: ["cyu", "tyu"],
                cho: ["cyo", "tyo"]
            };

            if (aliasMap[key]) {
                aliasMap[key].forEach((alias) => {
                    if (!acc[alias]) {
                        acc[alias] = item.symbol;
                    }
                });
            }
        }
        return acc;
    }, {});
}

function canMatch(romajiMap, text) {
    for (let len = Math.min(3, text.length); len >= 1; len--) {
        const part = text.slice(0, len);
        if (romajiMap[part]) return true;
    }
    return false;
}

function consumeSokuon(remainder, romajiMap, smallTsu) {
    const isDoubledConsonant =
        remainder.length > 1 &&
        remainder[0] === remainder[1] &&
        consonantPattern.test(remainder[0]) &&
        remainder[0] !== "n";
    const isTch = remainder.startsWith("tch");

    if (!isDoubledConsonant && !isTch) {
        return null;
    }

    // Drop only the consonant represented by the sokuon. This leaves the
    // complete following mora in place: kka -> ka and tcha -> cha.
    const next = remainder.slice(1);
    if (!canMatch(romajiMap, next)) {
        return null;
    }

    return {
        produced: smallTsu,
        remainder: next
    };
}

function consumeN(remainder, isKatakana) {
    if (
        remainder.length <= 1 ||
        remainder[0] !== "n" ||
        vowelPattern.test(remainder[1]) ||
        remainder[1] === "y"
    ) {
        return null;
    }

    return {
        produced: isKatakana ? "ン" : "ん",
        remainder: remainder.slice(1)
    };
}

function consumeMatchedSymbol(remainder, romajiMap) {
    for (let len = Math.min(3, remainder.length); len >= 1; len--) {
        const part = remainder.slice(0, len);
        const symbol = romajiMap[part];
        if (symbol) {
            return {
                produced: symbol,
                remainder: remainder.slice(len)
            };
        }
    }
    return null;
}

function consumeKatakanaLongVowel(remainder, lastVowel, isKatakana) {
    if (!isKatakana || !lastVowel || remainder[0] !== lastVowel) {
        return null;
    }

    return {
        produced: "ー",
        remainder: remainder.slice(1)
    };
}

// commitTrailingN controls whether a lone, unresolved trailing "n" gets
// committed to ん immediately. That's correct once the full word is known
// (e.g. "hon" -> ほん), but wrong for incremental/live typing: a bare "n"
// might just be the first half of a doubled "nn" (e.g. "konnichiwa"), and
// committing it too early makes the second "n" combine as a *second* ん
// instead of completing the first one. Live callers should pass false and
// decide for themselves when a pending "n" is truly final (see
// App.jsx's debounced commit).
function produceKana(value, romajiMap, isKatakana, commitTrailingN = true) {
    let remainder = String(value || "").toLowerCase();
    let produced = "";
    let lastVowel = "";
    const smallTsu = isKatakana ? "ッ" : "っ";

    const handlers = [
        (text) => {
            if (!text.startsWith("nn")) return null;
            return {
                produced: isKatakana ? "ン" : "ん",
                remainder: text.slice(1)
            };
        },
        (text) => consumeSokuon(text, romajiMap, smallTsu),
        (text) => consumeN(text, isKatakana),
        (text) => consumeKatakanaLongVowel(text, lastVowel, isKatakana),
        (text) => consumeMatchedSymbol(text, romajiMap)
    ];

    while (remainder.length > 0) {
        // ん has a direct romaji mapping ("n" -> ん), so without this guard
        // a bare "n" would resolve via the ordinary symbol-matching handler
        // below - not just the post-loop fallback - the moment it's the
        // only thing left, even mid-word with commitTrailingN: false.
        if (!commitTrailingN && remainder === "n") break;

        let result = null;
        for (const handler of handlers) {
            result = handler(remainder);
            if (result) break;
        }

        if (!result) break;

        const consumedLength = remainder.length - result.remainder.length;
        const lastConsumedChar = remainder[consumedLength - 1];

        produced += result.produced;
        remainder = result.remainder;
        lastVowel = vowelPattern.test(lastConsumedChar) ? lastConsumedChar : "";
    }

    if (remainder === "n" && commitTrailingN) {
        produced += isKatakana ? "ン" : "ん";
        remainder = "";
    }

    return { produced, remainder };
}

export function convertRomajiValue(value, romajiMap, isKatakana = false, commitTrailingN = true) {
    return produceKana(value, romajiMap, isKatakana, commitTrailingN);
}

// A handful of common words are conventionally spelled with は (topic
// particle, pronounced "wa" here) rather than the わ a literal romaji
// transliteration would produce. The converter still produces the literal
// form - getRomajiIdiomCorrection lets callers show a "did you mean" hint
// alongside it instead of silently rewriting what the user typed.
const konnichiwaCorrection = {
    correct: "こんにちは",
    note: "The greeting uses the topic particle は, pronounced wa."
};

const idiomCorrections = {
    konnichiwa: konnichiwaCorrection,
    konichiwa: konnichiwaCorrection,
    konbanwa: {
        correct: "こんばんは",
        note: "The greeting uses the topic particle は, pronounced wa."
    }
};

export function getRomajiIdiomCorrection(romajiToken) {
    const key = String(romajiToken || "").toLowerCase().trim();
    return idiomCorrections[key] || null;
}

const punctuationMap = {
    ",": "、",
    ".": "。",
    "?": "？",
    "!": "！",
    "-": "ー"
};

export function convertRomajiSentence(text, romajiMap, isKatakana = false) {
    const tokens = String(text || "").match(/[a-zA-Z]+|[^a-zA-Z]+/g) || [];

    return tokens.reduce((segments, token) => {
        if (/^[a-zA-Z]+$/.test(token)) {
            const { produced, remainder } = produceKana(token.toLowerCase(), romajiMap, isKatakana);
            if (produced) {
                const correction = getRomajiIdiomCorrection(token);
                segments.push(correction ? { text: produced, converted: true, correction } : { text: produced, converted: true });
            }
            if (remainder) segments.push({ text: remainder, converted: false });
            return segments;
        }

        segments.push({
            text: token.replace(/[,.?!-]/g, (char) => punctuationMap[char] ?? char),
            converted: true
        });
        return segments;
    }, []);
}
